import React, { useState, useEffect } from 'react';
import { FaPencilAlt, FaTrash } from 'react-icons/fa';
import { api } from '../utils/api';
import { fmtDate, fmtHours, monthRange } from '../utils/helpers';
import { useAuth } from '../App';
import LogSession from './LogSession';

function SquawkBadge({ status }) {
  if (!status) return <span style={{color:'#aaa'}}>—</span>;
  const map = { open: ['badge-open','Open'], in_progress: ['badge-progress','In Progress'], resolved: ['badge-resolved','Resolved'] };
  const [cls, label] = map[status] || ['badge-open', status];
  return <span className={`badge ${cls}`}>{label}</span>;
}

export default function Sessions() {
  const { user } = useAuth();
  const [sessions,    setSessions]    = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [filters,     setFilters]     = useState(() => monthRange());
  const [editSession, setEditSession] = useState(null);
  const [loading,     setLoading]     = useState(false);

  useEffect(() => {
    api.instructors.list().then(setInstructors).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [filters]);

  async function load() {
    setLoading(true);
    try { setSessions(await api.sessions.list(filters)); } catch {}
    setLoading(false);
  }

  function setFilter(k, v) {
    setFilters(f => ({ ...f, [k]: v || undefined }));
  }

  async function deleteSession(id) {
    if (!window.confirm('Delete this session? This cannot be undone.')) return;
    await api.sessions.delete(id);
    load();
  }

  if (editSession) {
    return (
      <LogSession
        editData={editSession}
        onSaved={() => { setEditSession(null); load(); }}
        onCancel={() => setEditSession(null)}
      />
    );
  }

  return (
    <div>
      <div className="section-title">Session History</div>

      <div className="filter-bar">
        <div className="form-group">
          <label>From</label>
          <input type="date" value={filters.from || ''} onChange={e => setFilter('from', e.target.value)} />
        </div>
        <div className="form-group">
          <label>To</label>
          <input type="date" value={filters.to || ''} onChange={e => setFilter('to', e.target.value)} />
        </div>
        <div className="form-group">
          <label>Instructor</label>
          <select value={filters.instructor_id || ''} onChange={e => setFilter('instructor_id', e.target.value)}>
            <option value="">All</option>
            {instructors.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
          </select>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={() => setFilters(monthRange())}>Reset</button>
      </div>

      <div className="card">
        <div className="card-header">
          <h2>Sessions ({sessions.length})</h2>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? (
            <div className="empty">Loading...</div>
          ) : sessions.length === 0 ? (
            <div className="empty"><div className="empty-icon">📋</div>No sessions found</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Instructor</th>
                    <th>Crew</th>
                    <th>Type</th>
                    <th>Start</th>
                    <th>Stop</th>
                    <th>Lab</th>
                    <th>Sim</th>
                    <th>Squawk</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map(s => (
                    <tr key={s.id}>
                      <td>{fmtDate(s.date)}</td>
                      <td>
                        <div>{s.instructor_name}</div>
                        <div style={{fontSize:11,color:'#888'}}>{s.instructor_code}</div>
                      </td>
                      <td>
                        {s.pic_name && (
                          <div style={{fontWeight:600}}>
                            PIC: {s.pic_name}
                            {s.single_pilot ? <span style={{fontSize:11,color:'#888',marginLeft:4}}>(solo)</span> : null}
                          </div>
                        )}
                        {s.sic_name && (
                          <div style={{fontSize:12,color:'#555'}}>SIC: {s.sic_name}</div>
                        )}
                        {!s.pic_name && <span style={{color:'#aaa'}}>—</span>}
                      </td>
                      <td><span className="badge badge-type">{s.training_type}</span></td>
                      <td>{s.start_time}</td>
                      <td>{s.end_time}</td>
                      <td style={{fontWeight:600}}>{fmtHours(s.lab_time)}</td>
                      <td style={{fontWeight:600}}>{fmtHours(s.sim_time)}</td>
                      <td><SquawkBadge status={s.squawk ? s.squawk_status : null} /></td>
                      <td style={{whiteSpace:'nowrap'}}>
                        <button className="btn btn-secondary btn-icon" title="Edit session" onClick={() => setEditSession(s)}>
                          <FaPencilAlt />
                        </button>
                        {user.role === 'admin' && (
                          <button className="btn btn-danger btn-icon" style={{marginLeft:4}} title="Delete session" onClick={() => deleteSession(s.id)}>
                            <FaTrash />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div style={{color:'#888', fontSize:12}}>
        Lab Time: {fmtHours(sessions.reduce((a, s) => a + Number(s.lab_time), 0))} hrs &nbsp;|&nbsp;
        Sim Time: {fmtHours(sessions.reduce((a, s) => a + Number(s.sim_time), 0))} hrs &nbsp;|&nbsp;
        {sessions.length} sessions
      </div>
    </div>
  );
}
